/**
 * Panel del operador de plataforma (doc 03 Fase 3, doc 06 §1.4): instituciones,
 * licencias, subdominios del portal y administradores/auditores. Funcional,
 * sin pulido visual — mismo criterio que `frontend/src/Expediente.tsx` en su
 * momento (Fase 1).
 */
import { useState, useEffect } from 'react';
import type {
  AdministradorResumen,
  CapacidadOperador,
  Estatuto,
  Licencia,
  Organismo,
  OrganismoConfig,
  Rol,
  TipoOrganismo,
} from '@sumarios/shared';
import {
  advertenciaConflictoInteres,
  motivoSubdominioInvalido,
  sugerirSubdominio,
  urlPortal,
} from '@sumarios/shared';
import * as api from './api.js';
import { config } from './config.js';
import { mensajeError } from './App.js';
import { GestionServicio, SECCIONES, type SeccionServicio } from './GestionServicio.js';

import { EquipoOperador } from './EquipoOperador.js';

const TIPOS: TipoOrganismo[] = ['municipalidad', 'hospital', 'servicio_salud', 'otro'];
const ESTATUTOS: Estatuto[] = ['ley_18883', 'ley_18834'];
const ROLES_PLATAFORMA: Rol[] = ['ADMIN_ORG', 'AUDITOR'];

function vigenciaTexto(licencia: Licencia): { texto: string; vencidaSinMarcar: boolean } {
  const dias = Math.ceil((Date.parse(licencia.termino) - Date.now()) / (24 * 60 * 60 * 1000));
  const vencidaSinMarcar = dias < 0 && licencia.estado !== 'vencida';
  const texto = dias >= 0 ? `${dias} días restantes` : `vencida hace ${-dias} días`;
  return { texto, vencidaSinMarcar };
}

/** Enlace del portal si hay slug + template; si no, solo el slug o un aviso de que falta. */
function PortalDe({ organismo }: { organismo: Organismo }) {
  const [copiado, setCopiado] = useState(false);
  if (!organismo.subdominio) {
    return <span className="ayuda">sin subdominio asignado</span>;
  }
  const url = urlPortal(config.portalUrlTemplate, organismo.subdominio);
  if (!url) {
    // Dev sin dominio wildcard: solo mostramos el slug, no hay URL real que copiar.
    return <span className="folio">{organismo.subdominio}</span>;
  }
  return (
    <span className="fila-entre">
      <a href={url} target="_blank" rel="noreferrer">
        {organismo.subdominio}
      </a>
      <button
        type="button"
        className="secundario"
        onClick={() => {
          void navigator.clipboard.writeText(url).then(() => {
            setCopiado(true);
            setTimeout(() => setCopiado(false), 1500);
          });
        }}
      >
        {copiado ? 'Copiado ✓' : 'Copiar enlace'}
      </button>
    </span>
  );
}

export function PanelOperador({ idToken, onSalir }: { idToken: string; onSalir: () => void }) {
  const [seccion, setSeccion] = useState<SeccionServicio>('resumen');
  const [capacidades, setCapacidades] = useState<CapacidadOperador[]>([]);
  const [organismos, setOrganismos] = useState<Organismo[] | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [seleccionado, setSeleccionado] = useState<string | null>(null);
  const [mostrarAlta, setMostrarAlta] = useState(false);

  function recargar() {
    setError(undefined);
    Promise.all([api.listarOrganismos(idToken), api.getMe(idToken)])
      .then(([lista, me]) => {
        setOrganismos(lista.organismos);
        setCapacidades(me.operador.capacidades);
      })
      .catch((e: unknown) => setError(mensajeError(e)));
  }
  useEffect(recargar, [idToken]);

  const organismoSeleccionado = organismos?.find((o) => o.orgId === seleccionado);

  return (
    <main className="app operador-app">
      <header className="app-header">
        <div className="app-header-titulo">
          <img src="/logo-intikipu.png" alt="IntiKipu" className="logo-img" />
          <div>
            <h1>Instructor — Operador</h1>
            <p className="subtitulo">Clientes, contratos, facturación y soporte</p>
          </div>
        </div>
        <div className="app-header-acciones">
          <button className="secundario" onClick={onSalir}>
            Salir
          </button>
        </div>
      </header>

      <div className="app-main">
        {config.ambiente === 'demo' && <div className="banner-demo">PANEL DEL ENTORNO DEMO</div>}

        {error && (
          <p role="alert" className="error">
            {error}
          </p>
        )}
        {!organismos && !error && <p className="ayuda">Cargando…</p>}

        {organismos && (
          <nav className="gestion-nav" aria-label="Administración del servicio">
            {SECCIONES.filter((s) => !s.capacidad || capacidades.includes(s.capacidad)).map((s) => (
              <button
                key={s.id}
                className={seccion === s.id ? '' : 'secundario'}
                aria-current={seccion === s.id ? 'page' : undefined}
                onClick={() => setSeccion(s.id)}
              >
                {s.nombre}
              </button>
            ))}
          </nav>
        )}
        {organismos && seccion === 'clientes' && (
          <div className="tarjeta col">
            <div className="fila-entre">
              <h2>Instituciones ({organismos.length})</h2>
              {capacidades.includes('clientes.gestionar') && (
                <button
                  className="secundario boton-agregar"
                  onClick={() => setMostrarAlta((v) => !v)}
                >
                  {mostrarAlta ? 'Cancelar' : '+ Nueva institución'}
                </button>
              )}
            </div>
            {mostrarAlta && capacidades.includes('clientes.gestionar') && (
              <NuevoOrganismo
                idToken={idToken}
                onCreado={() => {
                  setMostrarAlta(false);
                  recargar();
                }}
              />
            )}
            {organismos.length === 0 ? (
              <p className="ayuda">Sin instituciones todavía.</p>
            ) : (
              <div className="tabla-scroll" role="region" aria-label="Instituciones" tabIndex={0}>
                <table className="tabla tabla-instituciones">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Portal</th>
                      <th>Licencia</th>
                      <th>Vigencia</th>
                      <th>Plan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {organismos.map((o) => {
                      const { texto, vencidaSinMarcar } = vigenciaTexto(o.licencia);
                      return (
                        <tr
                          key={o.orgId}
                          className={seleccionado === o.orgId ? 'fila-seleccionada' : undefined}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <button className="secundario" onClick={() => setSeleccionado(o.orgId)}>
                              {o.nombre}
                            </button>
                          </td>
                          <td>
                            <PortalDe organismo={o} />
                          </td>
                          <td>
                            <span className="chip">{o.licencia.estado}</span>
                          </td>
                          <td>
                            {texto}
                            {vencidaSinMarcar && <span className="error"> · sin marcar</span>}
                          </td>
                          <td>{o.licencia.plan}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {seccion === 'clientes' &&
          capacidades.includes('clientes.gestionar') &&
          organismoSeleccionado && (
            <GestionOrganismo
              key={organismoSeleccionado.orgId}
              idToken={idToken}
              organismo={organismoSeleccionado}
              onActualizado={recargar}
            />
          )}
        {organismos && seccion === 'equipo' && capacidades.includes('operadores.gestionar') && (
          <EquipoOperador token={idToken} />
        )}
        {organismos && seccion !== 'equipo' && (
          <GestionServicio
            key={seccion}
            token={idToken}
            organismos={organismos}
            capacidades={capacidades}
            seccion={seccion}
            orgId={seleccionado}
            onOrg={setSeleccionado}
            onClienteActualizado={recargar}
          />
        )}
      </div>
    </main>
  );
}

function NuevoOrganismo({ idToken, onCreado }: { idToken: string; onCreado: () => void }) {
  const [nombre, setNombre] = useState('');
  const [rut, setRut] = useState('');
  const [tipo, setTipo] = useState<TipoOrganismo>('municipalidad');
  const [comuna, setComuna] = useState('');
  const [region, setRegion] = useState('');
  const [estatutoDefecto, setEstatutoDefecto] = useState<Estatuto>('ley_18883');
  const [subdominio, setSubdominio] = useState('');
  const [subdominioTocado, setSubdominioTocado] = useState(false);
  const [sigla, setSigla] = useState('');
  const [formatoNumeracion, setFormatoNumeracion] = useState('SUM-{ANIO}-{CORR}-{SIGLA}');
  const [plan, setPlan] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminNombre, setAdminNombre] = useState('');
  const [adminRut, setAdminRut] = useState('');
  const [adminCargo, setAdminCargo] = useState('');
  const [evidenciaIdentidad, setEvidenciaIdentidad] = useState('');
  const [evidenciaArchivo, setEvidenciaArchivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Sugiere el subdominio a partir del nombre mientras el operador no lo haya
  // tocado a mano — apenas lo edita, dejamos de tocarlo por él.
  function cambiarNombre(v: string) {
    setNombre(v);
    if (!subdominioTocado) setSubdominio(sugerirSubdominio(v));
  }

  const errorSubdominio = subdominio ? motivoSubdominioInvalido(subdominio) : null;

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(undefined);
    try {
      if (!evidenciaArchivo)
        throw new Error('Adjunte el PDF del decreto/resolución de designación');
      if (errorSubdominio) throw new Error(errorSubdominio);
      const { uploadUrl, s3Key } = await api.solicitarUploadUrlEvidencia(
        idToken,
        evidenciaArchivo.type,
        evidenciaArchivo.size,
      );
      await api.subirArchivo(uploadUrl, evidenciaArchivo);

      const config: OrganismoConfig = { formatoNumeracion, sigla };
      await api.crearOrganismo(idToken, {
        nombre,
        rut,
        tipo,
        comuna,
        region,
        estatutoDefecto,
        config,
        subdominio,
        plan,
        adminEmail,
        adminNombre,
        adminRut,
        adminCargo,
        evidenciaIdentidad,
        evidenciaS3Key: s3Key,
      });
      onCreado();
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="subtarjeta col" onSubmit={enviar}>
      <h3>Nueva institución</h3>
      <div className="fila">
        <label>
          Nombre
          <input value={nombre} onChange={(e) => cambiarNombre(e.target.value)} required />
        </label>
        <label>
          RUT
          <input value={rut} onChange={(e) => setRut(e.target.value)} required />
        </label>
      </div>
      <label>
        Subdominio del portal
        <input
          value={subdominio}
          onChange={(e) => {
            setSubdominio(e.target.value);
            setSubdominioTocado(true);
          }}
          placeholder="ej. cobquecura"
          required
        />
      </label>
      {subdominio && (
        <p className={errorSubdominio ? 'error' : 'ayuda'}>
          {errorSubdominio ??
            (config.portalUrlTemplate
              ? `Portal: ${urlPortal(config.portalUrlTemplate, subdominio)}`
              : `Slug: ${subdominio}`)}
        </p>
      )}
      <div className="fila">
        <label>
          Tipo
          <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoOrganismo)}>
            {TIPOS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label>
          Estatuto por defecto
          <select
            value={estatutoDefecto}
            onChange={(e) => setEstatutoDefecto(e.target.value as Estatuto)}
          >
            {ESTATUTOS.map((es) => (
              <option key={es} value={es}>
                {es}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="fila">
        <label>
          Comuna
          <input value={comuna} onChange={(e) => setComuna(e.target.value)} required />
        </label>
        <label>
          Región
          <input value={region} onChange={(e) => setRegion(e.target.value)} required />
        </label>
      </div>
      {comuna && region && advertenciaConflictoInteres(comuna, region) && (
        <p className="error">{advertenciaConflictoInteres(comuna, region)}</p>
      )}
      <div className="fila">
        <label>
          Sigla de numeración
          <input value={sigla} onChange={(e) => setSigla(e.target.value)} required />
        </label>
        <label>
          Formato de numeración
          <input
            value={formatoNumeracion}
            onChange={(e) => setFormatoNumeracion(e.target.value)}
            required
          />
        </label>
        <label>
          Plan
          <input value={plan} onChange={(e) => setPlan(e.target.value)} required />
        </label>
      </div>

      <h4>Primer administrador (ADMIN_ORG)</h4>
      <div className="fila">
        <label>
          Correo
          <input
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            autoComplete="off"
            required
          />
        </label>
        <label>
          Nombre completo
          <input value={adminNombre} onChange={(e) => setAdminNombre(e.target.value)} required />
        </label>
      </div>
      <div className="fila">
        <label>
          RUT
          <input value={adminRut} onChange={(e) => setAdminRut(e.target.value)} required />
        </label>
        <label>
          Cargo
          <input value={adminCargo} onChange={(e) => setAdminCargo(e.target.value)} required />
        </label>
      </div>
      <label>
        Referencia del decreto/resolución
        <input
          value={evidenciaIdentidad}
          onChange={(e) => setEvidenciaIdentidad(e.target.value)}
          placeholder="Ej: Decreto Alcaldicio N°45/2026 que lo designa"
          required
        />
      </label>
      <label>
        PDF del decreto/resolución de designación
        <input
          type="file"
          accept="application/pdf"
          onChange={(e) => setEvidenciaArchivo(e.target.files?.[0] ?? null)}
          required
        />
      </label>

      {error && <p className="error">{error}</p>}
      <button type="submit" disabled={enviando || !!errorSubdominio}>
        {enviando ? 'Creando…' : 'Crear institución'}
      </button>
    </form>
  );
}

function GestionOrganismo({
  idToken,
  organismo,
  onActualizado,
}: {
  idToken: string;
  organismo: Organismo;
  onActualizado: () => void;
}) {
  return (
    <div className="tarjeta col">
      <h2>{organismo.nombre}</h2>
      <EditarSubdominio idToken={idToken} organismo={organismo} onActualizado={onActualizado} />
      <EditarLicencia idToken={idToken} organismo={organismo} onActualizado={onActualizado} />
      <GestionAdministradores idToken={idToken} orgId={organismo.orgId} />
    </div>
  );
}

function EditarSubdominio({
  idToken,
  organismo,
  onActualizado,
}: {
  idToken: string;
  organismo: Organismo;
  onActualizado: () => void;
}) {
  const [subdominio, setSubdominio] = useState(organismo.subdominio ?? '');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [guardado, setGuardado] = useState(false);

  const cambio = subdominio !== (organismo.subdominio ?? '');
  const errorSubdominio = subdominio ? motivoSubdominioInvalido(subdominio) : 'Requerido.';

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(undefined);
    setGuardado(false);
    try {
      await api.actualizarSubdominio(idToken, organismo.orgId, { subdominio, motivo });
      setGuardado(true);
      setMotivo('');
      onActualizado();
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="subtarjeta" onSubmit={guardar}>
      <h3>Subdominio del portal</h3>
      {!organismo.subdominio && (
        <p className="error">Esta institución no tiene subdominio asignado todavía.</p>
      )}
      <div className="fila">
        <label>
          Subdominio
          <input value={subdominio} onChange={(e) => setSubdominio(e.target.value)} required />
        </label>
        {cambio && (
          <label>
            Motivo (queda en el audit log)
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} required />
          </label>
        )}
      </div>
      {subdominio && errorSubdominio && <p className="error">{errorSubdominio}</p>}
      {config.portalUrlTemplate && subdominio && !errorSubdominio && (
        <p className="ayuda">Portal: {urlPortal(config.portalUrlTemplate, subdominio)}</p>
      )}
      {error && <p className="error">{error}</p>}
      {guardado && <p className="ayuda">Subdominio actualizado.</p>}
      <button type="submit" disabled={enviando || !cambio || !!errorSubdominio || !motivo.trim()}>
        {enviando ? 'Guardando…' : 'Guardar subdominio'}
      </button>
    </form>
  );
}

function EditarLicencia({
  idToken,
  organismo,
  onActualizado,
}: {
  idToken: string;
  organismo: Organismo;
  onActualizado: () => void;
}) {
  const [estado, setEstado] = useState<Licencia['estado']>(organismo.licencia.estado);
  const [plan, setPlan] = useState(organismo.licencia.plan);
  const [renovarDias, setRenovarDias] = useState('');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [guardado, setGuardado] = useState(false);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(undefined);
    setGuardado(false);
    try {
      const termino = renovarDias.trim()
        ? new Date(Date.now() + Number(renovarDias) * 24 * 60 * 60 * 1000).toISOString()
        : undefined;
      await api.actualizarLicencia(idToken, organismo.orgId, {
        estado,
        plan,
        ...(termino ? { termino } : {}),
        motivo,
      });
      setGuardado(true);
      setMotivo('');
      onActualizado();
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="subtarjeta" onSubmit={guardar}>
      <h3>Licencia</h3>
      <div className="fila">
        <label>
          Estado
          <select value={estado} onChange={(e) => setEstado(e.target.value as Licencia['estado'])}>
            <option value="activa">activa</option>
            <option value="suspendida">suspendida</option>
            <option value="vencida">vencida</option>
          </select>
        </label>
        <label>
          Plan
          <input value={plan} onChange={(e) => setPlan(e.target.value)} required />
        </label>
        <label>
          Renovar por (días)
          <input
            type="number"
            min={1}
            value={renovarDias}
            onChange={(e) => setRenovarDias(e.target.value)}
            placeholder="ej. 365"
          />
        </label>
      </div>
      <label>
        Motivo (queda en el audit log)
        <input value={motivo} onChange={(e) => setMotivo(e.target.value)} required />
      </label>
      {error && <p className="error">{error}</p>}
      {guardado && <p className="ayuda">Licencia actualizada.</p>}
      <button type="submit" disabled={enviando}>
        {enviando ? 'Guardando…' : 'Guardar licencia'}
      </button>
    </form>
  );
}

function GestionAdministradores({ idToken, orgId }: { idToken: string; orgId: string }) {
  const [administradores, setAdministradores] = useState<AdministradorResumen[] | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [mostrarAlta, setMostrarAlta] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);
  const [revocando, setRevocando] = useState<string | null>(null);

  function recargar() {
    api
      .listarAdministradores(idToken, orgId)
      .then((r) => setAdministradores(r.administradores))
      .catch((e: unknown) => setError(mensajeError(e)));
  }
  useEffect(recargar, [idToken, orgId]);

  return (
    <div className="subtarjeta">
      <div className="fila-entre">
        <h3>Administradores y auditores</h3>
        <button className="secundario boton-agregar" onClick={() => setMostrarAlta((v) => !v)}>
          {mostrarAlta ? 'Cancelar' : '+ Nuevo'}
        </button>
      </div>
      {error && <p className="error">{error}</p>}
      {mostrarAlta && (
        <NuevoAdministrador
          idToken={idToken}
          orgId={orgId}
          onCreado={() => {
            setMostrarAlta(false);
            recargar();
          }}
        />
      )}
      {!administradores && !error && <p className="ayuda">Cargando…</p>}
      {administradores && administradores.length === 0 && (
        <p className="ayuda">Sin administradores/auditores.</p>
      )}
      {administradores && administradores.length > 0 && (
        <ul className="lista-actuaciones">
          {administradores.map((a) => (
            <li key={a.userId}>
              {editando === a.userId ? (
                <EditarRolesAdministrador
                  idToken={idToken}
                  orgId={orgId}
                  administrador={a}
                  onListo={() => {
                    setEditando(null);
                    recargar();
                  }}
                  onCancelar={() => setEditando(null)}
                />
              ) : revocando === a.userId ? (
                <RevocarAcceso
                  idToken={idToken}
                  orgId={orgId}
                  administrador={a}
                  onListo={() => {
                    setRevocando(null);
                    recargar();
                  }}
                  onCancelar={() => setRevocando(null)}
                />
              ) : (
                <div className="fila-entre">
                  <div>
                    <strong>{a.nombre}</strong>
                    <span className="folio">{a.email}</span>
                    <div className="chips">
                      {a.roles.map((r) => (
                        <span key={r} className="chip">
                          {r}
                        </span>
                      ))}
                      <span className="chip">{a.estado}</span>
                    </div>
                  </div>
                  <div className="fila">
                    <button className="secundario" onClick={() => setEditando(a.userId)}>
                      Editar roles
                    </button>
                    <button className="secundario" onClick={() => setRevocando(a.userId)}>
                      {a.estado === 'activo' ? 'Revocar acceso' : 'Reactivar acceso'}
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Confirmación inline con motivo, en vez de `window.confirm`/`window.prompt`:
 * ambos cuelgan el navegador en contextos embebidos/iframes sandboxeados
 * (lección ya aprendida en `Expediente.tsx` con `window.confirm`, doc
 * AWS_PHASE_1) — mejor práctica general, no solo para ese caso puntual.
 */
function RevocarAcceso({
  idToken,
  orgId,
  administrador,
  onListo,
  onCancelar,
}: {
  idToken: string;
  orgId: string;
  administrador: AdministradorResumen;
  onListo: () => void;
  onCancelar: () => void;
}) {
  const nuevoEstado = administrador.estado === 'activo' ? 'suspendido' : 'activo';
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function confirmar() {
    setEnviando(true);
    setError(undefined);
    try {
      await api.actualizarEstadoAdministrador(idToken, orgId, administrador.userId, {
        estado: nuevoEstado,
        motivo,
      });
      onListo();
    } catch (e) {
      setError(mensajeError(e));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="col">
      <strong>
        {nuevoEstado === 'suspendido' ? 'Revocar' : 'Reactivar'} acceso de {administrador.nombre}
      </strong>
      <label>
        Motivo (queda en el audit log)
        <input value={motivo} onChange={(e) => setMotivo(e.target.value)} autoFocus required />
      </label>
      {error && <p className="error">{error}</p>}
      <div className="fila-entre">
        <button type="button" className="secundario" disabled={enviando} onClick={onCancelar}>
          Cancelar
        </button>
        <button type="button" disabled={enviando || !motivo.trim()} onClick={confirmar}>
          {enviando
            ? 'Guardando…'
            : nuevoEstado === 'suspendido'
              ? 'Revocar acceso'
              : 'Reactivar acceso'}
        </button>
      </div>
    </div>
  );
}

function EditarRolesAdministrador({
  idToken,
  orgId,
  administrador,
  onListo,
  onCancelar,
}: {
  idToken: string;
  orgId: string;
  administrador: AdministradorResumen;
  onListo: () => void;
  onCancelar: () => void;
}) {
  const [roles, setRoles] = useState<Rol[]>(administrador.roles);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | undefined>();

  function alternarRol(rol: Rol) {
    setRoles((prev) => (prev.includes(rol) ? prev.filter((r) => r !== rol) : [...prev, rol]));
  }

  async function guardar() {
    setEnviando(true);
    setError(undefined);
    try {
      await api.editarRolesAdministrador(idToken, orgId, administrador.userId, { roles, motivo });
      onListo();
    } catch (e) {
      setError(mensajeError(e));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="col">
      <strong>{administrador.nombre}</strong>
      <fieldset>
        <legend>Roles</legend>
        {ROLES_PLATAFORMA.map((rol) => (
          <label key={rol} className="fila-check">
            <input
              type="checkbox"
              checked={roles.includes(rol)}
              onChange={() => alternarRol(rol)}
            />
            {rol}
          </label>
        ))}
      </fieldset>
      <label>
        Motivo (queda en el audit log)
        <input value={motivo} onChange={(e) => setMotivo(e.target.value)} required />
      </label>
      {error && <p className="error">{error}</p>}
      <div className="fila-entre">
        <button type="button" className="secundario" disabled={enviando} onClick={onCancelar}>
          Cancelar
        </button>
        <button
          type="button"
          disabled={enviando || roles.length === 0 || !motivo.trim()}
          onClick={guardar}
        >
          {enviando ? 'Guardando…' : 'Guardar roles'}
        </button>
      </div>
    </div>
  );
}

function NuevoAdministrador({
  idToken,
  orgId,
  onCreado,
}: {
  idToken: string;
  orgId: string;
  onCreado: () => void;
}) {
  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [rut, setRut] = useState('');
  const [cargo, setCargo] = useState('');
  const [rol, setRol] = useState<Rol>('ADMIN_ORG');
  const [evidenciaIdentidad, setEvidenciaIdentidad] = useState('');
  const [evidenciaArchivo, setEvidenciaArchivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(undefined);
    try {
      if (!evidenciaArchivo)
        throw new Error('Adjunte el PDF del decreto/resolución de designación');
      const { uploadUrl, s3Key } = await api.solicitarUploadUrlEvidencia(
        idToken,
        evidenciaArchivo.type,
        evidenciaArchivo.size,
      );
      await api.subirArchivo(uploadUrl, evidenciaArchivo);

      await api.crearAdministrador(idToken, orgId, {
        email,
        nombre,
        rut,
        cargo,
        rol,
        evidenciaIdentidad,
        evidenciaS3Key: s3Key,
      });
      onCreado();
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="subtarjeta" onSubmit={enviar}>
      <h4>Nuevo administrador/auditor</h4>
      <label>
        Correo
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="off"
          required
        />
      </label>
      <div className="fila">
        <label>
          Nombre completo
          <input value={nombre} onChange={(e) => setNombre(e.target.value)} required />
        </label>
        <label>
          RUT
          <input value={rut} onChange={(e) => setRut(e.target.value)} required />
        </label>
      </div>
      <div className="fila">
        <label>
          Cargo
          <input value={cargo} onChange={(e) => setCargo(e.target.value)} required />
        </label>
        <label>
          Rol
          <select value={rol} onChange={(e) => setRol(e.target.value as Rol)}>
            {ROLES_PLATAFORMA.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label>
        Referencia del decreto/resolución
        <input
          value={evidenciaIdentidad}
          onChange={(e) => setEvidenciaIdentidad(e.target.value)}
          placeholder="Ej: Decreto Alcaldicio N°45/2026 que lo designa"
          required
        />
      </label>
      <label>
        PDF del decreto/resolución de designación
        <input
          type="file"
          accept="application/pdf"
          onChange={(e) => setEvidenciaArchivo(e.target.files?.[0] ?? null)}
          required
        />
      </label>
      {error && <p className="error">{error}</p>}
      <button type="submit" disabled={enviando}>
        {enviando ? 'Creando…' : 'Crear'}
      </button>
    </form>
  );
}
